import React, { useContext } from 'react';
import { AppContext } from '../context/AppContext';
import { ShoppingCart, User, Lightbulb, LogOut } from 'lucide-react';
import { motion } from 'framer-motion';

// Import the logo from your assets folder (Must be a Transparent PNG!)
import logoImg from '../assets/logo.png'; 

const Navbar = ({ onAuthClick }) => {
    const { darkMode, toggleDarkMode, cart, setIsCartOpen, user, logout } = useContext(AppContext);

    return (
        <nav className="sticky top-0 z-50 px-4 md:px-8 py-3 backdrop-blur-2xl bg-white/5 dark:bg-black/30 border-b border-white/10 shadow-lg">
            <div className="max-w-[1800px] mx-auto flex justify-between items-center">
                
                {/* 1. Integrated Graphical Logo Section (No Name) */}
                <motion.div 
                    onClick={() => window.location.href = '/'}
                    className="cursor-pointer p-1.5 group relative"
                    whileHover={{ scale: 1.05 }} // Subtle entrance animation
                    initial={{ opacity: 0, x: -10 }} // entrance
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ type: "spring", stiffness: 300, damping: 20 }}
                >
                    {/* Hover Glow Effect */}
                    <div className="absolute -inset-1 bg-red-600/30 blur-lg rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    
                    {/* The Logo with Integrated Styling */}
                    <img 
                        src={logoImg} 
                        alt="Swift Advanced" 
                        className="relative h-11 w-auto object-contain brightness-110 drop-shadow-[0_0_8px_rgba(239,68,68,0.4)] transition-all duration-300 group-hover:drop-shadow-[0_0_12px_rgba(34,197,94,0.6)]"
                    />
                </motion.div>

                {/* 2. Actions Section (Theme, Cart, Login) */}
                <div className="flex items-center gap-3 md:gap-6">
                    {/* Theme Toggle */}
                    <button onClick={toggleDarkMode} className="p-2.5 rounded-full hover:bg-yellow-400/10 transition-all">
                        <Lightbulb size={22} className={darkMode ? "text-yellow-400 fill-yellow-400" : "text-gray-400"} />
                    </button>

                    {/* Cart Icon */}
                    <div className="relative cursor-pointer p-2.5 rounded-full hover:bg-white/5" onClick={() => setIsCartOpen(true)}>
                        <ShoppingCart size={22} />
                        {cart.length > 0 && (
                            <span className="absolute top-1 right-1 bg-red-600 text-[10px] w-5 h-5 rounded-full flex items-center justify-center font-bold animate-pulse text-white">
                                {cart.length}
                            </span>
                        )}
                    </div>

                    {/* Auth / Profile Section */}
                    <div className="flex items-center gap-2">
                        {!user ? (
                            <button 
                                onClick={onAuthClick}
                                className="flex items-center gap-2 bg-gradient-to-r from-red-600 to-red-700 text-white px-5 py-2.5 rounded-full text-sm font-black hover:from-red-700 hover:to-red-800 transition-all shadow-lg shadow-red-500/20 active:scale-95"
                            >
                                <User size={18} /> LOGIN
                            </button>
                        ) : (
                            <div className="flex items-center gap-3">
                                <div className="hidden md:flex items-center gap-2 bg-green-500/10 px-4 py-2.5 rounded-full border border-green-500/20">
                                    <div className="w-2 h-2 bg-green-500 rounded-full animate-ping"></div>
                                    <span className="text-sm font-bold text-green-500">
                                        {user.name.split(' ')[0]}
                                    </span>
                                </div>
                                
                                <button 
                                    onClick={logout}
                                    className="p-2 text-gray-400 hover:text-red-500 transition-colors group"
                                    title="Logout"
                                >
                                    <LogOut size={22} className="group-hover:translate-x-1 transition-transform" />
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </nav>
    );
};

export default Navbar;